import React, { useMemo, useState } from "react";
import Widget, { spanFor } from "./widgets";
import StoreVerdict from "./StoreVerdict";
import { widgetKind } from "./dashboard-api";
import { packRows } from "./packRows";
import useWidgetData, { partitionWidgets } from "./useWidgetData";

/**
 * Renders a New Relic dashboard page for the clicked store.
 *
 * Widgets that returned something get cards and reflow to fill the grid;
 * everything that came back empty drops out of the grid into a strip of chips
 * below it. Keeping the two apart means a short widget never shares a row with
 * a tall one, so there are no holes to manage — and nothing is hidden, since
 * every quiet widget is still named and can be opened in place.
 */
/**
 * These pages are named "Store Entity <thing> Status" in New Relic; the panel
 * header already says which entity and dashboard this is, so the prefix is
 * noise here.
 */
function pageLabel(name) {
  return String(name || "Dashboard").replace(/^\s*(store\s+)?entity\s+/i, "").trim() || "Dashboard";
}

export default function DashboardView({
  page,
  source,
  variables,
  strategies,
  fallbackAccountId,
}) {
  const results = useWidgetData(page.widgets, {
    variables,
    strategies,
    fallbackAccountId,
  });

  const { loud, quiet, pending } = useMemo(
    () => partitionWidgets(page.widgets, results),
    [page.widgets, results]
  );

  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const opened = quiet.filter((q) => expanded.has(q.widget.id));

  // Bin widgets into rows of at most 12 shares, then let each row divide its
  // full width between the widgets in it.
  const rows = useMemo(
    () =>
      packRows(
        loud.map((item) => ({
          ...item,
          span: spanFor(widgetKind(item.widget), item.result.rows),
        }))
      ),
    [loud]
  );

  return (
    <>
      <StoreVerdict
        source={source}
        results={results}
        widgets={page.widgets}
      />

      {/* Open by default: the verdict answers the question, the grid backs it up. */}
      <details className="nrw-disclosure" open>
        <summary>{pageLabel(page.name)}</summary>

        <div className="nrw-panel">
          {rows.length ? (
            <div className="nrw-rows">
              {rows.map((row, i) => (
                <div
                  className="nrw-row"
                  key={row.map((r) => r.widget.id).join("|") || i}
                  style={{ "--nrw-row-h": Math.max(...row.map((r) => r.span.h)) }}
                >
                  {row.map(({ widget, result, span }) => (
                    <Widget key={widget.id} widget={widget} result={result} span={span} />
                  ))}
                </div>
              ))}
            </div>
          ) : null}

          {pending.length ? (
            <p className="nrw-pending">Running {pending.length} more…</p>
          ) : null}

          {quiet.length ? (
            <div className="nrw-quiet">
              <h5 className="nrw-quiet__title">
                Nothing to report ({quiet.length})
              </h5>
              <ul className="nrw-quiet__chips">
                {quiet.map(({ widget, failed }) => (
                  <li key={widget.id}>
                    <button
                      type="button"
                      className={`nrw-chip ${failed ? "nrw-chip--failed" : ""} ${
                        expanded.has(widget.id) ? "nrw-chip--open" : ""
                      }`}
                      onClick={() => toggle(widget.id)}
                      aria-expanded={expanded.has(widget.id)}
                      title={
                        failed
                          ? "This check failed to run — click for the error"
                          : "No data returned — click to open"
                      }
                    >
                      {failed ? <span className="nrw-chip__dot" aria-hidden="true" /> : null}
                      <span className="nrw-chip__label">{widget.title}</span>
                    </button>
                  </li>
                ))}
              </ul>

              {opened.length ? (
                <div className="nrw-rows nrw-rows--reopened">
                  {opened.map(({ widget, result }) => (
                    <div className="nrw-row" key={widget.id} style={{ "--nrw-row-h": 4 }}>
                      <Widget widget={widget} result={result} span={{ w: 12, h: 4 }} />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {!loud.length && !quiet.length && !pending.length ? (
            <p className="nrw-pending">This dashboard page has no widgets.</p>
          ) : null}
        </div>
      </details>
    </>
  );
}

export { widgetKind };
